import type {
  ConfigurationOperation,
  TestSignalProposalToolOutput,
} from "@repo/app-types/entities/configurationAgent";
import type { KnowledgeBaseAttachment } from "@repo/app-types/entities/knowledgeBase";
import { buildKnowledgeBaseDocumentName } from "@repo/app-types/entities/knowledgeRetrievalTool";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { PlaybookTree } from "@repo/app-types/entities/playbook";
import type { AdminApiPlaybookTestSpec } from "@repo/app-types/entities/teammateAgent";
import {
  findCriteria,
  findSection,
} from "@repo/app-types/helpers/playbookTreeOperations";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";

export interface TestSpecLookup {
  name: string;
  description: string;
  expectedOutcome?: string;
}

export interface ConfigurationAssistantContextValue {
  /** Apply an operation and mark it as applied */
  applyOperation: (
    operationId: string,
    operation: ConfigurationOperation,
  ) => Promise<void>;
  /** Check if an operation has already been applied */
  isOperationApplied: (operationId: string) => boolean;
  /** Look up an entity name by ID and type */
  getEntityName: (
    id: string,
    type: "section" | "criteria" | "test-case" | "chapter" | "lesson",
  ) => string | undefined;
  /** Look up full test spec data by ID */
  getTestSpec: (id: string) => TestSpecLookup | undefined;
  /** The playbook's outcome definition, if configured */
  outcomeDefinition: OutcomeDefinition | undefined;
  /** Resolve a slugified KB document name to its display title */
  getDocumentDisplayName: (slugifiedName: string) => string | undefined;
  /** Callback when test signal proposal status changes (loading or complete) */
  onTestResultsLoading?: () => void;
  onTestResultsComplete?: (results: TestSignalProposalToolOutput) => void;
}

export const ConfigurationAssistantContext =
  createContext<ConfigurationAssistantContextValue | null>(null);

export interface ConfigurationAssistantContextProviderProps {
  onApplyOperation: (operation: ConfigurationOperation) => Promise<void> | void;
  /** The playbook tree for looking up section/criteria names */
  playbookTree?: PlaybookTree;
  /** Test specs for looking up test case names */
  testSpecs?: AdminApiPlaybookTestSpec[];
  /** The playbook's outcome definition, if configured */
  outcomeDefinition?: OutcomeDefinition;
  /** KB attachments for resolving slugified document names to display titles */
  knowledgeBaseAttachments?: readonly KnowledgeBaseAttachment[];
  children: ReactNode;
}

export const ConfigurationAssistantContextProvider: FC<
  ConfigurationAssistantContextProviderProps
> = ({
  onApplyOperation,
  playbookTree,
  testSpecs,
  outcomeDefinition,
  knowledgeBaseAttachments,
  children,
}) => {
  // Track which operations have been applied using their stable IDs
  const [appliedOperationIds, setAppliedOperationIds] = useState(
    () => new Set<string>(),
  );

  const applyOperation = useEffectEvent(
    async (
      operationId: string,
      operation: ConfigurationOperation,
    ): Promise<void> => {
      // Check synchronously first to avoid applying twice
      if (appliedOperationIds.has(operationId)) {
        return; // Already applied, no-op
      }
      // Call side effect outside the state updater to avoid React violations
      await onApplyOperation(operation);
      // Update state after side effect
      setAppliedOperationIds((prev) => new Set(prev).add(operationId));
    },
  );

  const isOperationApplied = useCallback(
    (operationId: string) => appliedOperationIds.has(operationId),
    [appliedOperationIds],
  );

  // Build lookup maps for test specs by ID
  const testSpecsNameMap = useMemo(() => {
    if (!testSpecs) return new Map<string, string>();
    return new Map(testSpecs.map((spec) => [spec.id, spec.name]));
  }, [testSpecs]);

  const testSpecsFullMap = useMemo(() => {
    if (!testSpecs) return new Map<string, TestSpecLookup>();
    return new Map(
      testSpecs.map((spec) => {
        // Extract expected outcome value from the complex outcomes structure
        const outcomeValue = spec.expected?.outcomes?.preset
          ? Object.values(spec.expected.outcomes.preset)[0]?.value
          : spec.expected?.outcomes?.custom
            ? Object.values(spec.expected.outcomes.custom)[0]?.value
            : undefined;

        return [
          spec.id,
          {
            name: spec.name,
            description: spec.description,
            expectedOutcome: outcomeValue,
          },
        ];
      }),
    );
  }, [testSpecs]);

  const getEntityName = useCallback(
    (
      id: string,
      type: "section" | "criteria" | "test-case" | "chapter" | "lesson",
    ): string | undefined => {
      switch (type) {
        case "section":
          if (!playbookTree) return undefined;
          return findSection(playbookTree, id)?.name;
        case "criteria":
          if (!playbookTree) return undefined;
          return findCriteria(playbookTree, id)?.name;
        case "test-case":
          return testSpecsNameMap.get(id);
        case "chapter":
        case "lesson":
          return undefined;
      }
    },
    [playbookTree, testSpecsNameMap],
  );

  const getTestSpec = useCallback(
    (id: string): TestSpecLookup | undefined => {
      return testSpecsFullMap.get(id);
    },
    [testSpecsFullMap],
  );

  const getDocumentDisplayName = useCallback(
    (slugifiedName: string): string | undefined => {
      const attachment = knowledgeBaseAttachments?.find(
        (a) =>
          buildKnowledgeBaseDocumentName({ title: a.title }) === slugifiedName,
      );
      return attachment?.title ?? attachment?.file_name ?? undefined;
    },
    [knowledgeBaseAttachments],
  );

  const contextValue: ConfigurationAssistantContextValue = useMemo(
    () => ({
      applyOperation,
      isOperationApplied,
      getEntityName,
      getTestSpec,
      outcomeDefinition,
      getDocumentDisplayName,
    }),
    [
      applyOperation,
      isOperationApplied,
      getEntityName,
      getTestSpec,
      outcomeDefinition,
      getDocumentDisplayName,
    ],
  );

  return (
    <ConfigurationAssistantContext.Provider value={contextValue}>
      {children}
    </ConfigurationAssistantContext.Provider>
  );
};

export function useConfigurationAssistantContext(): ConfigurationAssistantContextValue {
  const context = useContext(ConfigurationAssistantContext);
  if (!context) {
    throw new Error(
      "useConfigurationAssistantContext must be used within ConfigurationAssistantContextProvider",
    );
  }
  return context;
}
